/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
'use strict';

import { useContext, useEffect, useRef } from 'react';
import { StatusContext, AsyncOpEntry } from './StatusContext';

interface AsyncStatusPanelProps {
    panelRef: React.RefObject<HTMLDivElement>;
    onClose: () => void;
}

function AsyncOpCard({ op }: { op: AsyncOpEntry }) {
    const outputRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = outputRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [op.stdout, op.stderr]);

    const statusColor = !op.done ? '#f0a500'
        : op.returnCode === 0 ? '#22c55e'
        : '#ef4444';

    return (
        <div style={{ margin: '6px 8px', border: '1px solid #30363d', borderRadius: 4 }}>
            <div style={{
                background: '#161b22', padding: '4px 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '1px solid #30363d',
            }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600, color: '#e6edf3' }}>
                    {op.label}
                </span>
                <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: statusColor, display: 'inline-block', flexShrink: 0,
                }} />
            </div>
            {op.cmd && (
                <div style={{
                    padding: '2px 10px', fontFamily: 'monospace', fontSize: '0.75rem',
                    color: '#8b949e', borderBottom: '1px solid #21262d',
                }}>
                    $ {op.cmd}
                </div>
            )}
            {op.active && !op.done && (
                <div style={{ padding: '6px 10px', borderBottom: '1px solid #21262d' }}>
                    <div style={{ height: 4, background: '#21262d', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{
                            height: '100%',
                            width: op.pct === null ? '100%' : `${op.pct}%`,
                            background: op.pct === null
                                // Nothing to measure, so the bar reads as busy
                                // rather than claiming a position it does not know
                                ? 'linear-gradient(90deg, #21262d, #58a6ff, #21262d)'
                                : '#58a6ff',
                            backgroundSize: op.pct === null ? '200% auto' : undefined,
                            animation: op.pct === null ? 'shimmer 1.2s linear infinite' : undefined,
                            borderRadius: 2,
                            transition: 'width 200ms linear',
                        }} />
                    </div>
                    <div style={{
                        marginTop: 3, fontFamily: 'monospace', fontSize: '0.72rem', color: '#8b949e',
                        display: 'flex', justifyContent: 'space-between', gap: 8,
                    }}>
                        <span>{op.progressText}</span>
                        {op.pct !== null && <span>{op.pct}%</span>}
                    </div>
                </div>
            )}
            <div
                ref={outputRef}
                style={{
                    maxHeight: '200px', overflowY: 'auto', padding: '4px 10px',
                    fontFamily: 'monospace', fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}
            >
                {op.stdout && (
                    <div style={{ color: '#e6edf3' }}>{op.stdout}</div>
                )}
                {op.stderr && (
                    <div style={{ color: '#f85149' }}>{op.stderr}</div>
                )}
                {!op.stdout && !op.stderr && !op.active && !op.done && (
                    <div style={{ color: '#8b949e', fontStyle: 'italic' }}>Running…</div>
                )}
            </div>
            {op.done && (
                <div style={{
                    padding: '2px 10px', borderTop: '1px solid #21262d',
                    fontFamily: 'monospace', fontSize: '0.72rem', color: statusColor,
                }}>
                    {/*
                      * A shell command's exit status is the useful thing to
                      * report; a task that never ran one has no exit code worth
                      * showing, so it gets its closing message instead.
                      */}
                    {op.cmd && !op.active
                        ? `exit ${op.returnCode}`
                        : `${op.returnCode === 0 ? '✓' : '✗'} ${op.progressText
                            || (op.returnCode === 0 ? 'done' : 'failed')}`
                            + ` · ${((op.lastTs - op.ts) / 1000).toFixed(1)}s`}
                </div>
            )}
        </div>
    );
}

export default function AsyncStatusPanel({ panelRef, onClose }: AsyncStatusPanelProps) {
    const statusContext = useContext(StatusContext);
    const ops = statusContext.asyncOps;

    return (
        <div
            ref={panelRef}
            style={{
                position: 'fixed',
                top: '48px',
                right: 0,
                width: '600px',
                height: 'calc(100vh - 48px)',
                background: '#0d1117',
                color: '#e6edf3',
                borderLeft: '1px solid #30363d',
                borderTop: '1px solid #30363d',
                borderRadius: '6px 0 0 0',
                boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
                zIndex: 1090,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'hidden',
            }}
        >
            <div style={{
                background: '#161b22', borderBottom: '1px solid #30363d',
                padding: '6px 12px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexShrink: 0,
            }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600 }}>
                    Async Operations
                </span>
                <button
                    type="button"
                    onClick={onClose}
                    style={{
                        background: 'none', border: 'none', color: '#e6edf3',
                        cursor: 'pointer', fontSize: '1rem', lineHeight: 1,
                    }}
                    aria-label="Close panel"
                >
                    ✕
                </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '6px 0' }}>
                {ops.length === 0 && (
                    <div style={{ padding: '12px 14px', color: '#8b949e', fontStyle: 'italic', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        No operations yet.
                    </div>
                )}
                {ops.map(op => (
                    <AsyncOpCard key={op.id} op={op} />
                ))}
            </div>
        </div>
    );
}
